import { useState } from "react";
import { Button, Card, CardBody, CardHeader, Metric } from "@odingard/design-system";
import { EmptyState, ErrorState, LoadingState } from "@odingard/design-system";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../lib/api";

export function Seats() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const state = useAsync(() => api.subscription(orgId), [orgId]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const sub = state.data?.subscription;

  async function changeSeats(next: number) {
    if (!sub || next < sub.assigned_seats || next < 1) return;
    setBusy(true);
    setNotice(null);
    try {
      await api.updateSeats(orgId, sub.id, next);
      setNotice(`Seats updated to ${next}.`);
      await state.reload();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Could not update seats.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Seats</h1>
          <p>Add or remove licensed seats for your organization.</p>
        </div>
      </div>
      {notice && <div className="banner banner--info">{notice}</div>}
      {state.loading && <LoadingState />}
      {state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}
      {state.data &&
        (!sub ? (
          <EmptyState icon="👥" title="No subscription" description="Add a plan before managing seats." />
        ) : (
          <>
            <div className="metric-grid">
              <div className="metric-card">
                <Metric value={sub.seats} label="Total seats" />
              </div>
              <div className="metric-card">
                <Metric value={sub.assigned_seats} label="Assigned" />
              </div>
              <div className="metric-card">
                <Metric value={sub.seats - sub.assigned_seats} label="Available" />
              </div>
            </div>
            <Card>
              <CardHeader title="Adjust seats" />
              <CardBody>
                <div className="ods-row">
                  <Button variant="secondary" disabled={busy || sub.seats <= sub.assigned_seats} onClick={() => changeSeats(sub.seats - 1)}>
                    −
                  </Button>
                  <span style={{ fontSize: "var(--ods-text-2xl)", fontWeight: 800, minWidth: 48, textAlign: "center" }}>{sub.seats}</span>
                  <Button variant="secondary" disabled={busy} onClick={() => changeSeats(sub.seats + 1)}>
                    +
                  </Button>
                </div>
                <p style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>
                  You can&apos;t reduce below assigned seats ({sub.assigned_seats}). Seat changes are prorated by the provider.
                </p>
              </CardBody>
            </Card>
          </>
        ))}
    </>
  );
}
